// =============================================================================
// Conditional Result Page  —  route: /result
// Shows one of three outcomes based on the scored qualification tier.
// Qualified applicants are asked to send a resume, cover letter, and a short
// plan for their first 3 clients (on-page and by email). Other tiers do not
// get next-step instructions and never see a booking link.
// =============================================================================

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { TIER, CONTACT_EMAIL } from './funnelConfig';
import { trackEvent } from './tracking';

// Analytics event fired when each result page is viewed.
const RESULT_EVENT = {
  [TIER.QUALIFIED]: 'qualified_result_viewed',
  [TIER.NURTURE]: 'nurture_result_viewed',
  [TIER.NOT_FIT]: 'not_fit_result_viewed',
};

const OUTCOMES = {
  [TIER.QUALIFIED]: {
    badge: 'Qualified',
    badgeClass: 'bg-green-100 text-green-700',
    headline: "Congratulations — You've Qualified",
    copy:
      'Based on your answers, you appear to have the entrepreneurial mindset we look for at Revolution Travel.',
  },
  [TIER.NURTURE]: {
    badge: 'Under review',
    badgeClass: 'bg-amber-100 text-amber-700',
    headline: "You May Be a Fit, But We'd Like to Learn More",
    copy:
      'Your answers show some potential alignment. We will review your application and follow up if there appears to be a strong fit.',
  },
  [TIER.NOT_FIT]: {
    badge: 'Not a fit right now',
    badgeClass: 'bg-gray-200 text-gray-700',
    headline: 'This May Not Be the Right Fit Right Now',
    copy:
      'Revolution Travel is looking for people who want to build a serious business, not simply access travel perks or discounts. Based on your answers, this may not be the right opportunity at this time.',
  },
};

// The three items a qualified applicant must send to advance.
const QUALIFIED_ITEMS = [
  'Your resume.',
  'A short cover letter — tell us why you want to build a travel business and what makes you a strong fit for a commission-based, entrepreneurial role.',
  'A short paragraph explaining how you would generate your first 3 clients.',
];

/** Build a pre-filled mailto: link for the qualified next-step submission. */
function buildResumeMailto(lead) {
  const name = `${lead.firstName || ''} ${lead.lastName || ''}`.trim();
  const subject = `Entrepreneur Application — ${name || 'Qualified Applicant'}`;
  const body =
    `Hi Sean,\n\n` +
    `I qualified through the Revolution Travel entrepreneur application. As the ` +
    `next step, I'm sending:\n\n` +
    `1. My resume (attached).\n` +
    `2. My cover letter (attached or below).\n` +
    `3. How I would generate my first 3 clients:\n\n` +
    `[ Write your plan for your first 3 clients here ]\n\n` +
    `${name}`;
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** Next-step card shown to Qualified applicants. */
function QualifiedNextStep({ lead }) {
  return (
    <div className="mt-8 rounded-xl border border-revolution-blue/30 bg-blue-50 p-6 text-left sm:p-7">
      <span className="inline-block rounded-full bg-revolution-blue px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
        Next step
      </span>
      <h2 className="mt-3 text-xl font-bold text-revolution-navy sm:text-2xl">
        Send us three things
      </h2>
      <p className="mt-2 text-gray-700">
        To move your application forward, email us the items below. Once we review them, our team
        will reach out about next steps.
      </p>

      <ul className="mt-4 space-y-2.5">
        {QUALIFIED_ITEMS.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-revolution-blue text-xs font-bold text-white">
              {i + 1}
            </span>
            <span className="text-gray-700">{item}</span>
          </li>
        ))}
      </ul>

      <p className="mt-4 rounded-lg bg-white/70 p-3 text-sm italic text-revolution-navy">
        This final step helps us identify applicants who think like business builders, not casual
        travel fans.
      </p>

      <a
        href={buildResumeMailto(lead)}
        className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-revolution-orange px-7 py-4 text-base font-bold text-white shadow-lg transition hover:bg-orange-600 sm:w-auto"
      >
        Email {CONTACT_EMAIL}
      </a>

      {lead.emailSent ? (
        <p className="mt-4 text-sm text-gray-600">
          We&rsquo;ve also emailed these instructions to{' '}
          <span className="font-semibold">{lead.email}</span> — check your inbox (and spam folder).
        </p>
      ) : (
        <p className="mt-4 text-sm text-gray-600">
          Please send the three items above to{' '}
          <span className="font-semibold">{CONTACT_EMAIL}</span>.
        </p>
      )}
    </div>
  );
}

function ResultPage() {
  const location = useLocation();
  const result = location.state && location.state.result;
  const tier = result && result.tier;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (tier) {
      trackEvent(RESULT_EVENT[tier] || 'not_fit_result_viewed', {
        score: result.score,
        flags: result.flags || [],
      });
    }
  }, [tier, result]);

  // Direct hit / refresh with no submission in router state — send back to start.
  if (!result) {
    return <Navigate to="/" replace />;
  }

  const outcome = OUTCOMES[tier] || OUTCOMES[TIER.NOT_FIT];
  const isQualified = tier === TIER.QUALIFIED;

  return (
    <div className="landing-page bg-revolution-gray">
      <Header />

      <main className="mx-auto max-w-2xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="rounded-2xl bg-white p-7 shadow-lg sm:p-10">
          <span
            className={`inline-block rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${outcome.badgeClass}`}
          >
            {outcome.badge}
          </span>

          <h1 className="mt-4 text-3xl font-extrabold text-revolution-navy sm:text-4xl">
            {outcome.headline}
          </h1>

          <p className="mt-4 text-lg leading-relaxed text-gray-600">{outcome.copy}</p>

          {result.firstName && (
            <p className="mt-6 text-gray-700">
              Thanks for applying, <span className="font-semibold">{result.firstName}</span>. Your
              application has been received.
            </p>
          )}

          {isQualified ? (
            <QualifiedNextStep lead={result} />
          ) : (
            <div className="mt-8 rounded-xl bg-revolution-gray p-6">
              <p className="text-gray-700">
                {tier === TIER.NURTURE
                  ? 'No further action is needed right now — keep an eye on your inbox.'
                  : 'We appreciate the time you took to apply, and we wish you the best on your journey.'}
              </p>
              <a
                href="https://www.revolutiontravel.ca"
                className="mt-4 inline-flex items-center font-semibold text-revolution-blue hover:text-blue-700"
              >
                Visit revolutiontravel.ca
                <svg className="ml-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ResultPage;
